import { useIntl } from 'react-intl';

import type { FlexProps } from '@/components/Flex';
import { Flex } from '@/components/Flex';
import type { TypographyScales } from '@/components/Typography';
import { Typography } from '@/components/Typography';
import type { MoneyFragment } from '@/graphql/saleor/api';
import type { CURRENCY_DISPLAY } from '@/lib/intl';
import type { Maybe } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './Price.styles';

export type PriceSize = 'xl' | 'l' | 'm' | 's' | 'inherited';
export type PriceColorVariant = 'primaryBlue' | 'default' | 'defaultInverted';

export interface PriceProps extends FlexProps {
  className?: string;
  colorVariant?: PriceColorVariant;
  displayFreeIfUndefined?: boolean;
  displayZero?: boolean;
  formatPriceOpts?: {
    currencyDisplay?: CURRENCY_DISPLAY;
    maximumFractionDigits?: number;
  };
  price?: Maybe<MoneyFragment>;
  priceUndiscounted?: Maybe<MoneyFragment>;
  scale?: TypographyScales;
  size?: PriceSize;
}

export const Price = ({
  color = 'primaryDark',
  displayFreeIfUndefined = false,
  displayZero,
  formatPriceOpts,
  price,
  priceUndiscounted,
  scale = 'p3',
  size = 'm',
  ...flexProps
}: PriceProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const shouldDisplayFree =
    (!price && displayFreeIfUndefined) || (price?.amount === 0 && !displayZero);

  if (!price && !displayFreeIfUndefined) return null;

  if (shouldDisplayFree) {
    return (
      <S.PriceFreeValue>
        <Typography scale='h5' textAlign='end'>
          {formatMessage({
            id: 'checkout.shoppingBag.free',
          })}
        </Typography>
      </S.PriceFreeValue>
    );
  }

  return (
    <Flex data-test-id='PriceFlex' {...flexProps}>
      <S.Price color={color} data-test-id='PriceText' scale={scale} size={size}>
        {formatPrice(price, formatPriceOpts)}
      </S.Price>
      {!!priceUndiscounted && (
        <Typography
          color={color}
          data-test-id='PriceUndiscountedText'
          ml='s050'
          scale={scale}
          textDecoration='line-through'
        >
          {formatPrice(priceUndiscounted, formatPriceOpts)}
        </Typography>
      )}
    </Flex>
  );
};

Price.displayName = 'ProductPrice';
